import { Box, Flex } from '@/components/atoms/Grid'
import React, { FC } from 'react'
import styled from 'styled-components'
import { RattingSizeIconDefault } from '@/components/atoms/icons/RattingSizeIconDefault'
import { RattingColorIconDefault } from '@/components/atoms/icons/RattingColorIconDefault'
import { RattingMaterialIconDefault } from '@/components/atoms/icons/RattingMaterialIconDefault'
import { Tiny } from '@/components/atoms/typography'
import { validFor } from '@/theme/validFor'
import { MarketCode, Product } from '@/network/graphql.g'
import useTranslation from 'next-translate/useTranslation'

import { useDomain } from '@/providers/storeProvider'

type RattingInfoSummaryProps = {
  product: Product
}

type ProductRateRecommended = {
  size: string | null
  color: string | null
  material: string | null
}

const RattingInfoSummary: FC<RattingInfoSummaryProps> = ({ product }) => {
  const { t } = useTranslation('common')
  const { subdomainName } = useDomain()
  const isProductReviewsDisabled = [
    MarketCode.Lv,
    MarketCode.Pl,
    MarketCode.Lt
  ].includes(subdomainName as MarketCode)

  type rattingInfoSummaryDataProps = {
    id: number
    title: string
    ratingInfo: string
    IconElement: JSX.Element
  }

  const getTranslate = (
    categoryType: string,
    ratingInfoText?: string
  ): string => {
    if (!ratingInfoText) {
      return ''
    }
    return (
      'ProductDetail.RattingReview.' +
      categoryType +
      '.' +
      ratingInfoText.charAt(0).toLowerCase() +
      ratingInfoText.slice(1)
    )
  }

  const rattingInfoSummaryData = (): rattingInfoSummaryDataProps[] => {
    return [
      {
        id: 1,
        title: t('Category.Filter.size'),
        ratingInfo: t(
          getTranslate('Size', product?.productRateRecommended?.size)
        ),
        IconElement: <RattingSizeIconDefault />
      },
      {
        id: 2,
        title: t('Category.Filter.color'),
        ratingInfo: t(
          getTranslate('Color', product?.productRateRecommended?.color)
        ),
        IconElement: <RattingColorIconDefault />
      },
      {
        id: 3,
        title: t('Category.Filter.material'),
        ratingInfo: t(
          getTranslate('Material', product?.productRateRecommended?.material)
        ),
        IconElement: <RattingMaterialIconDefault />
      }
    ]
  }

  const rattingInfoSummaryItems = rattingInfoSummaryData()

  const hasRecommendedRatings = (
    productRateRecommended: ProductRateRecommended
  ) => {
    return (
      productRateRecommended.size !== null ||
      productRateRecommended.color !== null ||
      productRateRecommended.material !== null
    )
  }

  return (
    <>
      {product?.productRateRecommended &&
      hasRecommendedRatings(
        product.productRateRecommended as ProductRateRecommended
      ) &&
      !isProductReviewsDisabled ? (
        <RattingInfoSummaryWrapper flexDirection="column">
          {rattingInfoSummaryItems.map((rattingInfoSummaryItem) =>
            rattingInfoSummaryItem?.ratingInfo ? (
              <RatingInfoItem key={rattingInfoSummaryItem?.id}>
                <Box mr={12}>{rattingInfoSummaryItem.IconElement}</Box>
                <StyledFlex
                  flexDirection={{
                    desktop: 'row',
                    tablet: 'row',
                    mobile: 'column'
                  }}
                >
                  <RattingInfoTitle>
                    {rattingInfoSummaryItem.title}
                  </RattingInfoTitle>
                  <RattingInfo>
                    {rattingInfoSummaryItem?.ratingInfo}
                  </RattingInfo>
                </StyledFlex>
              </RatingInfoItem>
            ) : null
          )}
        </RattingInfoSummaryWrapper>
      ) : null}
    </>
  )
}

export default RattingInfoSummary

const RattingInfoSummaryWrapper = styled(Flex)`
  flex-direction: column;
  border: ${(props) => `1px solid ${props.theme.colors.mobileBackground}`};
  padding: 15px 0;
`

const RatingInfoItem = styled(Flex)`
  margin-left: 12px;
  align-items: center;
  padding: 3px;
  ${validFor.mobile`
   align-items: flex-start;
  `}
  ${validFor.tablet`
  align-items: center;
  `}
`

const StyledFlex = styled(Flex)`
  align-items: center;
  ${validFor.mobile`
   line-height: 1.25;
   align-items: flex-start;
  `}
`

const RattingInfoTitle = styled(Tiny)`
  font-weight: 700;
  font-size: 12px;
  margin: 0 12px 0 0;
  min-width: 50px;
  ${validFor.tablet`
   line-height: 1.5;
  `}
  ${validFor.mobile`
   line-height: 1.5;
  `}
`
const RattingInfo = styled(Tiny)`
  font-weight: 400;
  font-size: 12px;
  margin: 0;
  ${validFor.tablet`
   line-height: 1.3;
  `}
  ${validFor.mobile`
   line-height: 1.3;
  `}
`
